/**
 * API Service - 与后端 FastAPI 接口对接
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Token management
let accessToken: string | null = null;
let refreshToken: string | null = null;

export const setTokens = (access: string, refresh: string) => {
  accessToken = access;
  refreshToken = refresh;
  localStorage.setItem('access_token', access);
  localStorage.setItem('refresh_token', refresh);
};

export const loadTokens = () => {
  accessToken = localStorage.getItem('access_token');
  refreshToken = localStorage.getItem('refresh_token');
};

export const clearTokens = () => {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

export const getAccessToken = () => accessToken;

// Generic fetch wrapper with auth
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const headers: HeadersInit = {
    ...options.headers,
  };
  
  // Add auth header if token exists
  if (accessToken) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
  }
  
  // Add content-type for JSON requests
  if (options.body && typeof options.body === 'string') {
    (headers as Record<string, string>)['Content-Type'] = 'application/json';
  }
  
  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // 关键！强制开启跨域 Session Cookie 回链
  });
  
  // Handle 401 - try refresh token
  if (response.status === 401 && refreshToken) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      // Retry original request
      (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
      const retryResponse = await fetch(url, { ...options, headers });
      if (!retryResponse.ok) {
        throw new ApiError(retryResponse.status, await retryResponse.json());
      }
      return retryResponse.json();
    }
  }
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(response.status, errorData);
  }
  
  return response.json();
}

async function refreshAccessToken(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    
    if (response.ok) {
      const data = await response.json();
      setTokens(data.access_token, data.refresh_token);
      return true;
    }
  } catch (e) {
    console.error('Token refresh failed:', e);
  }
  
  clearTokens();
  return false;
}

export class ApiError extends Error {
  status: number;
  code: string;
  details: Record<string, unknown>;
  
  constructor(status: number, data: { error?: { code?: string; message?: string; details?: Record<string, unknown> } }) {
    super(data.error?.message || 'API Error');
    this.status = status;
    this.code = data.error?.code || 'UNKNOWN';
    this.details = data.error?.details || {};
  }
}

// ============ Auth API ============

export const authApi = {
  getLoginUrl: () => apiFetch<{ login_url: string; auth_server: string; app_name: string }>('/auth/login-url'),
  
  getAuthStatus: () => apiFetch<{ unified_auth_enabled: boolean; dev_auth_enabled?: boolean; auth_mode?: string; auth_server: string; app_name: string }>('/auth/status'),
  
  getSessionUser: () => apiFetch<{ authenticated: boolean; user_info?: { id: string; name: string; email: string } }>('/auth/user'),
  
  verifyAzureToken: (jwtToken: string) => apiFetch<{ success: boolean; user_info: any; user: UserResponse; access_token: string; refresh_token: string }>(`/auth/verify?jwt=${encodeURIComponent(jwtToken)}`),
  
  refresh: (token: string) =>
    apiFetch<TokenResponse>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: token }),
    }),
  
  logout: () => apiFetch<{ success: boolean; azure_logout_url?: string }>('/auth/logout', { method: 'POST' }),
  
  me: () => apiFetch<UserResponse>('/auth/me'),
};

export interface LoginRedirectResponse {
  redirect_url: string;
}

export interface SuccessResponse {
  success: boolean;
  message?: string;
}

// ============ Projects API ============

export const projectsApi = {
  create: (data: ProjectCreateRequest) =>
    apiFetch<ProjectResponse>('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  list: (params?: { page?: number; page_size?: number; status?: string }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.page_size) query.set('page_size', String(params.page_size));
    if (params?.status) query.set('status', params.status);
    return apiFetch<PaginatedResponse<ProjectListItem>>(`/projects?${query}`);
  },
  
  get: (id: string) => apiFetch<ProjectDetailResponse>(`/projects/${id}`),
  
  update: (id: string, data: Partial<ProjectCreateRequest>) =>
    apiFetch<ProjectResponse>(`/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string) =>
    apiFetch<{ success: boolean }>(`/projects/${id}`, { method: 'DELETE' }),
    
  cancel: (id: string) =>
    apiFetch<{ success: boolean; message: string }>(`/projects/${id}/cancel`, { method: 'POST' }),
  
  getStatus: (id: string) => apiFetch<ProjectStatusResponse>(`/projects/${id}/status`),
};

// ============ Files API ============

export const filesApi = {
  upload: async (projectId: string, file: File, fileType: 'contract' | 'standard' | 'exception') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);
    
    const response = await fetch(`${API_BASE_URL}/projects/${projectId}/files`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      body: formData,
    });
    
    if (!response.ok) {
      throw new ApiError(response.status, await response.json());
    }
    
    return response.json() as Promise<FileUploadResponse>;
  },
  
  list: (projectId: string, fileType?: string) => {
    const query = fileType ? `?file_type=${fileType}` : '';
    return apiFetch<FileListItem[]>(`/projects/${projectId}/files${query}`);
  },
  
  get: (projectId: string, fileId: string) =>
    apiFetch<FileDetailResponse>(`/projects/${projectId}/files/${fileId}`),
  
  delete: (projectId: string, fileId: string) =>
    apiFetch<{ success: boolean }>(`/projects/${projectId}/files/${fileId}`, { method: 'DELETE' }),
};

// ============ Clauses API ============

export const clausesApi = {
  split: (projectId: string, options?: SplitRequest) =>
    apiFetch<SplitResponse>(`/projects/${projectId}/split`, {
      method: 'POST',
      body: JSON.stringify(options || {}),
    }),
  
  getSplitStatus: (projectId: string) =>
    apiFetch<SplitStatusResponse>(`/projects/${projectId}/split/status`),
  
  list: (projectId: string, params?: { page?: number; page_size?: number; version?: number }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.page_size) query.set('page_size', String(params.page_size));
    if (params?.version) query.set('version', String(params.version));
    return apiFetch<PaginatedResponse<ClauseListItem>>(`/projects/${projectId}/clauses?${query}`);
  },
  
  get: (projectId: string, clauseId: string) =>
    apiFetch<ClauseDetailResponse>(`/projects/${projectId}/clauses/${clauseId}`),
};

// ============ Exemptions API ============

export const exemptionsApi = {
  create: (projectId: string, data: ExemptionRuleCreate) =>
    apiFetch<ExemptionRuleResponse>(`/projects/${projectId}/exemptions`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  list: (projectId: string) =>
    apiFetch<ExemptionRuleListItem[]>(`/projects/${projectId}/exemptions`),
  
  get: (projectId: string, ruleId: string) =>
    apiFetch<ExemptionRuleResponse>(`/projects/${projectId}/exemptions/${ruleId}`),
  
  update: (projectId: string, ruleId: string, data: Partial<ExemptionRuleCreate>) =>
    apiFetch<ExemptionRuleResponse>(`/projects/${projectId}/exemptions/${ruleId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  delete: (projectId: string, ruleId: string) =>
    apiFetch<{ success: boolean }>(`/projects/${projectId}/exemptions/${ruleId}`, { method: 'DELETE' }),
};

// ============ Review API ============

export const reviewApi = {
  start: (projectId: string, options?: ReviewStartRequest) =>
    apiFetch<ReviewStartResponse>(`/projects/${projectId}/review`, {
      method: 'POST',
      body: JSON.stringify(options || {}),
    }),
  
  getStatus: (projectId: string) =>
    apiFetch<ReviewStatusResponse>(`/projects/${projectId}/review/status`),
  
  listResults: (projectId: string, params?: { page?: number; page_size?: number; status?: string }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.page_size) query.set('page_size', String(params.page_size));
    if (params?.status) query.set('status', params.status);
    return apiFetch<PaginatedResponse<ReviewResultListItem>>(`/projects/${projectId}/review/results?${query}`);
  },
  
  reviewClause: (projectId: string, clauseId: string, force?: boolean) =>
    apiFetch<ReviewResultResponse>(`/projects/${projectId}/clauses/${clauseId}/review`, {
      method: 'POST',
      body: JSON.stringify({ force: force || false }),
    }),
  
  getClauseResult: (projectId: string, clauseId: string) =>
    apiFetch<ReviewResultResponse>(`/projects/${projectId}/clauses/${clauseId}/review`),
};

// ============ Reports API ============

export const reportsApi = {
  generate: (projectId: string, includeUserEdits?: boolean) =>
    apiFetch<ReportGenerateResponse>(`/projects/${projectId}/report`, {
      method: 'POST',
      body: JSON.stringify({ include_user_edits: includeUserEdits ?? true }),
    }),
  
  get: (projectId: string) => apiFetch<ReportResponse>(`/projects/${projectId}/report`),
  
  list: (projectId: string) => apiFetch<ReportListItem[]>(`/projects/${projectId}/reports`),
};

// ============ User Edits API ============

export const userEditsApi = {
  create: (projectId: string, clauseId: string, data: UserEditCreate) =>
    apiFetch<UserEditResponse>(`/projects/${projectId}/clauses/${clauseId}/user_edits`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  list: (projectId: string, clauseId: string) =>
    apiFetch<UserEditResponse[]>(`/projects/${projectId}/clauses/${clauseId}/user_edits`),
};

// ============ Exports API ============

export const exportsApi = {
  create: (projectId: string, options: ExportCreateRequest) =>
    apiFetch<ExportResponse>(`/projects/${projectId}/export`, {
      method: 'POST',
      body: JSON.stringify(options),
    }),
  
  getStatus: (projectId: string, exportId: string) =>
    apiFetch<ExportStatusResponse>(`/projects/${projectId}/export/${exportId}`),
  
  list: (projectId: string) =>
    apiFetch<ExportListItem[]>(`/projects/${projectId}/exports`),
};

// ============ Standards API (Admin) ============

export const standardsApi = {
  create: (data: StandardCreateRequest) =>
    apiFetch<StandardResponse>('/standards', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  list: (params?: { page?: number; page_size?: number; active_only?: boolean }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.page_size) query.set('page_size', String(params.page_size));
    if (params?.active_only !== undefined) query.set('active_only', String(params.active_only));
    return apiFetch<PaginatedResponse<StandardListItem>>(`/standards?${query}`);
  },
  
  get: (id: string) => apiFetch<StandardDetailResponse>(`/standards/${id}`),
  
  update: (id: string, data: Partial<StandardCreateRequest>) =>
    apiFetch<StandardResponse>(`/standards/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  delete: (id: string) =>
    apiFetch<{ success: boolean }>(`/standards/${id}`, { method: 'DELETE' }),
  
  createRule: (standardId: string, data: StandardRuleCreate) =>
    apiFetch<StandardRuleResponse>(`/standards/${standardId}/rules`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updateRule: (standardId: string, ruleId: string, data: Partial<StandardRuleCreate>) =>
    apiFetch<StandardRuleResponse>(`/standards/${standardId}/rules/${ruleId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  deleteRule: (standardId: string, ruleId: string) =>
    apiFetch<{ success: boolean }>(`/standards/${standardId}/rules/${ruleId}`, { method: 'DELETE' }),
};

// ============ Disclaimer API ============

export const disclaimerApi = {
  get: () => apiFetch<{ disclaimer: { zh: string; en: string } }>('/disclaimer'),
};

// ============ Type Definitions ============

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface UserResponse {
  id: string;
  tpid: string;
  email: string;
  name: string;
  role: 'user' | 'admin';
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
}

export interface ProjectCreateRequest {
  name: string;
  description?: string;
  user_prompt?: string;
}

export interface ProjectResponse {
  id: string;
  name: string;
  description: string | null;
  status: ProjectStatus;
  user_prompt: string | null;
  error_message: string | null;
  clause_version: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectDetailResponse extends ProjectResponse {
  file_count: number;
  clause_count: number;
  review_progress: number;
}

export interface ProjectListItem {
  id: string;
  name: string;
  status: ProjectStatus;
  created_at: string;
  updated_at: string;
}

export interface ProjectStatusResponse {
  id: string;
  status: ProjectStatus;
  progress: number;
  current_stage: string | null;
  error_message: string | null;
}

export type ProjectStatus = 
  | 'created'
  | 'files_uploaded'
  | 'parsing'
  | 'parsed'
  | 'reviewing'
  | 'reviewed'
  | 'report_generating'
  | 'completed'
  | 'failed';

export interface FileUploadResponse {
  id: string;
  file_type: 'contract' | 'standard' | 'exception';
  original_name: string;
  mime_type: string;
  file_size: number;
  status: FileStatus;
  created_at: string;
}

export interface FileListItem {
  id: string;
  file_type: 'contract' | 'standard' | 'exception';
  original_name: string;
  file_size: number;
  status: FileStatus;
  created_at: string;
}

export interface FileDetailResponse extends FileUploadResponse {
  parsed_content: string | null;
  file_metadata: Record<string, unknown> | null;
  error_message: string | null;
  checksum: string;
}

export type FileStatus = 'uploaded' | 'parsing' | 'parsed' | 'failed';

export interface SplitRequest {
  strategy?: 'auto' | 'numbering' | 'paragraph' | 'semantic';
  min_clause_length?: number;
  max_clause_length?: number;
}

export interface SplitResponse {
  project_id: string;
  version: number;
  total_clauses: number;
  status: string;
  task_id: string | null;
}

export interface SplitStatusResponse {
  project_id: string;
  status: string;
  progress: number;
  total_clauses: number | null;
  error_message: string | null;
}

export interface ClauseListItem {
  id: string;
  sequence: number;
  clause_no: string | null;
  title: string | null;
  content_preview: string;
  version: number;
  clause_type: 'main_body' | 'supplementary';
}

export interface ClauseDetailResponse {
  id: string;
  sequence: number;
  clause_no: string | null;
  title: string | null;
  content: string;
  party_tags: string[] | null;
  source_page: number[] | null;
  version: number;
  clause_type: 'main_body' | 'supplementary';
  source_span: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface ExemptionRuleCreate {
  exemption_type: 'keyword' | 'clause_number' | 'clause_id' | 'text_match';
  name: string;
  keywords?: string[];
  clause_numbers?: string[];
  clause_ids?: string[];
  text_patterns?: string[];
  reason?: string;
}

export interface ExemptionRuleResponse {
  id: string;
  exemption_type: 'keyword' | 'clause_number' | 'clause_id' | 'text_match';
  name: string;
  keywords: string[] | null;
  clause_numbers: string[] | null;
  clause_ids: string[] | null;
  text_patterns: string[] | null;
  reason: string | null;
  source_file_id: string | null;
  created_at: string;
}

export interface ExemptionRuleListItem {
  id: string;
  exemption_type: 'keyword' | 'clause_number' | 'clause_id' | 'text_match';
  name: string;
  reason: string | null;
  created_at: string;
}

export interface ReviewStartRequest {
  standard_id?: string;
  use_exemptions?: boolean;
}

export interface ReviewStartResponse {
  project_id: string;
  task_id: string | null;
  status: string;
  total_clauses: number;
}

export interface ReviewStatusResponse {
  project_id: string;
  status: string;
  progress: number;
  current_clause: number | null;
  total_clauses: number;
  completed_count: number;
  error_count: number;
  current_stage: string | null;
  error_message: string | null;
}

export interface ReviewResultListItem {
  id: string;
  clause_id: string;
  clause_no: string | null;
  status: ClauseReviewStatus;
  confidence: number | null;
  has_issues: boolean;
}

export interface ReviewResultResponse {
  id: string;
  clause_id: string;
  status: ClauseReviewStatus;
  evidence: EvidenceItem[] | null;
  issues: string[] | null;
  suggested_cn: string | null;
  suggested_en: string | null;
  confidence: number | null;
  rationale: string | null;
  uncertain_reasons: string[] | null;
  missing_info: string[] | null;
  matched_rules: string[] | null;
  retry_count: number;
  created_at: string;
  updated_at: string;
}

export interface EvidenceItem {
  rule_id: string;
  rule_title: string;
  matched_text: string;
  relevance_score: number;
}

export type ClauseReviewStatus = 
  | 'pending'
  | 'compliant'
  | 'non_compliant'
  | 'uncertain'
  | 'ignored'
  | 'exempted';

export interface ReportGenerateResponse {
  project_id: string;
  task_id: string | null;
  status: string;
}

export interface ReportResponse {
  id: string;
  project_id: string;
  version: number;
  summary: ReportSummary;
  non_compliant_items: NonCompliantItem[];
  created_at: string;
}

export interface ReportSummary {
  total_clauses: number;
  compliant_count: number;
  non_compliant_count: number;
  uncertain_count: number;
  ignored_count: number;
  exempted_count: number;
  compliance_rate: number;
}

export interface NonCompliantItem {
  clause_id: string;
  clause_no: string | null;
  original_text: string;
  issues: string[];
  evidence: EvidenceItem[];
  suggested_cn: string | null;
  suggested_en: string | null;
  user_edit: string | null;
  confidence: number | null;
}

export interface ReportListItem {
  id: string;
  version: number;
  total_clauses: number;
  non_compliant_count: number;
  created_at: string;
}

export interface UserEditCreate {
  edit_lines: string[];
  final_text?: string;
  notes?: string;
}

export interface UserEditResponse {
  id: string;
  clause_id: string;
  user_id: string;
  edit_lines: string[];
  final_text: string | null;
  notes: string | null;
  version: number;
  created_at: string;
}

export interface ExportCreateRequest {
  format: 'docx' | 'pdf';
  options?: ExportOptions;
}

export interface ExportOptions {
  comparison_mode?: 'single' | 'dual_column' | 'track_changes';
  include_appendix?: boolean;
  include_summary?: boolean;
  user_edit_priority?: boolean;
}

export interface ExportResponse {
  id: string;
  project_id: string;
  format: 'docx' | 'pdf';
  status: ExportStatus;
  progress: number;
  current_stage: string | null;
  version: number;
  options: ExportOptions | null;
  error_message: string | null;
  download_url: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface ExportStatusResponse {
  id: string;
  status: ExportStatus;
  progress: number;
  current_stage: string | null;
  error_message: string | null;
  download_url: string | null;
  expires_at: string | null;
}

export interface ExportListItem {
  id: string;
  format: 'docx' | 'pdf';
  status: ExportStatus;
  version: number;
  created_at: string;
  download_url: string | null;
}

export type ExportStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface StandardCreateRequest {
  name: string;
  description?: string;
  is_default?: boolean;
}

export interface StandardResponse {
  id: string;
  name: string;
  description: string | null;
  version: number;
  is_active: boolean;
  is_default: boolean;
  rule_count: number;
  created_at: string;
  updated_at: string;
}

export interface StandardDetailResponse extends StandardResponse {
  rules: StandardRuleResponse[];
}

export interface StandardListItem {
  id: string;
  name: string;
  version: number;
  is_active: boolean;
  is_default: boolean;
  rule_count: number;
  created_at: string;
}

export interface StandardRuleCreate {
  rule_id: string;
  title: string;
  description: string;
  scope?: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  examples?: Array<{ type: string; description: string }>;
  keywords?: string[];
}

export interface StandardRuleResponse {
  id: string;
  rule_id: string;
  title: string;
  description: string;
  scope: string | null;
  severity: 'low' | 'medium' | 'high' | 'critical';
  examples: Array<{ type: string; description: string }> | null;
  keywords: string[] | null;
  sequence: number;
  created_at: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}
